import { Printer, X } from 'lucide-react';
import type { Traspaso } from '@/shared/api/tipos';

export function ActaDeTraspaso({ acta, alCerrar }: { acta: Traspaso; alCerrar: () => void }) {
  const numero = String(acta.id).padStart(6, '0');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fondo-del-dialogo absolute inset-0 bg-armazon-900/40 backdrop-blur-sm"
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Acta de traspaso ${numero}`}
        className="para-imprimir relative flex max-h-[95dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="sin-imprimir flex shrink-0 items-center justify-between gap-4 border-b border-armazon-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-semibold uppercase text-white transition-colors hover:bg-marca-700"
            >
              <Printer className="size-4" aria-hidden />
              Imprimir acta
            </button>
            <span className="text-[11px] uppercase text-armazon-400">
              O guárdela como PDF desde el diálogo de impresión
            </span>
          </div>

          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-10 py-8">
          <header className="mb-8 border-b-2 border-armazon-900 pb-4 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-armazon-500">
              EPAGAL EP
            </p>
            <p className="text-[10px] uppercase tracking-wide text-armazon-400">
              Empresa Pública de Aseo y Gestión Ambiental del Cantón Latacunga
            </p>
            <h1 className="mt-4 text-lg font-bold uppercase tracking-wide text-armazon-900">
              Acta de traspaso de cuenta
            </h1>
            <p className="importe mt-1 text-sm text-armazon-600">N.º {numero}</p>
          </header>

          <Bloque titulo="Cuenta de servicio">
            <Fila etiqueta="Número de cuenta" valor={acta.numeroCuenta} mono />
            <Fila etiqueta="Fecha del traspaso" valor={formatearFecha(acta.fecha)} />
          </Bloque>

          <Bloque titulo="Titular anterior">
            <Fila etiqueta="Nombre" valor={acta.nombreAnterior} />
            <Fila etiqueta="Cédula / RUC" valor={acta.identificacionAnterior} mono />
          </Bloque>

          <Bloque titulo="Titular nuevo">
            <Fila etiqueta="Nombre" valor={acta.nombreNuevo} />
            <Fila etiqueta="Cédula / RUC" valor={acta.identificacionNuevo} mono />
          </Bloque>

          <Bloque titulo="Motivo">
            <p className="col-span-2 text-sm leading-relaxed text-armazon-800">{acta.motivo}</p>
          </Bloque>

          <Bloque titulo="Situación de la deuda al momento del traspaso">

            {acta.deudaPendiente === 0 && acta.facturasPendientes === 0 ? (
              <p className="col-span-2 text-sm uppercase leading-relaxed text-armazon-800">
                No se registró deuda pendiente sobre esta cuenta en la fecha del traspaso.
              </p>
            ) : (
              <>
                <Fila
                  etiqueta="Importe pendiente"
                  valor={`$${acta.deudaPendiente.toFixed(2)}`}
                  mono
                />
                <Fila etiqueta="Facturas pendientes" valor={String(acta.facturasPendientes)} mono />
                <Fila etiqueta="Liquidada en este acto" valor={acta.deudaLiquidada ? 'Sí' : 'No'} />
              </>
            )}

            <p className="col-span-2 mt-2 hyphens-auto text-justify text-[11px] leading-relaxed text-armazon-500">
              La deuda registrada corresponde al predio y no al titular anterior. Las facturas ya
              emitidas conservan la identificación de quien era titular al emitirlas y no se
              reemiten a nombre del nuevo. Desde la siguiente emisión, la facturación de esta cuenta
              sale a nombre del titular nuevo.
            </p>
          </Bloque>

          <div className="mt-10 grid grid-cols-2 gap-10 pt-6">
            <Firma
              rotulo="Titular anterior"
              nombre={acta.nombreAnterior}
              identificacion={acta.identificacionAnterior}
            />
            <Firma
              rotulo="Titular nuevo"
              nombre={acta.nombreNuevo}
              identificacion={acta.identificacionNuevo}
            />
          </div>

          <p className="mt-10 border-t border-armazon-200 pt-3 text-[10px] uppercase text-armazon-400">
            Registrado por {acta.registradoPor ?? 'el sistema'}
            {acta.registradoEn && ` · ${formatearMomento(acta.registradoEn)}`}
          </p>
        </div>
      </div>
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 border-b border-armazon-200 pb-1 text-[11px] font-bold uppercase tracking-wider text-armazon-500">
        {titulo}
      </h2>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </section>
  );
}

function Fila({
  etiqueta,
  valor,
  mono = false,
}: {
  etiqueta: string;
  valor: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-armazon-400">{etiqueta}</p>
      <p className={`break-words text-sm text-armazon-900 ${mono ? 'importe font-medium' : ''}`}>
        {valor}
      </p>
    </div>
  );
}

function Firma({
  rotulo,
  nombre,
  identificacion,
}: {
  rotulo: string;
  nombre: string;
  identificacion: string;
}) {
  return (
    <div className="text-center">
      <div className="mb-1 h-12 border-b border-armazon-400" />
      <p className="text-xs font-medium uppercase text-armazon-800">{nombre}</p>
      <p className="importe text-[11px] text-armazon-600">{identificacion}</p>
      <p className="text-[10px] uppercase tracking-wider text-armazon-400">{rotulo}</p>
    </div>
  );
}

function formatearFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  const MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  const nombreDelMes = MESES[Number(mes) - 1] ?? mes;
  return `${dia} de ${nombreDelMes} de ${anio}`;
}

function formatearMomento(iso: string): string {
  const momento = new Date(iso);
  return Number.isNaN(momento.getTime()) ? iso : momento.toLocaleString('es-EC');
}
